import { useEffect, useRef, useState } from "react";
import { LogIn, RefreshCw } from "lucide-react";
import { useAuth } from "react-oidc-context";
import { Navigate, useLocation } from "react-router-dom";
import { Button } from "@nexus/ui";

import {
  forgetSession,
  pathOf,
  requiresSignin,
  returnToFromState,
  safeReturnTo,
} from "@/auth/session";
import { FullPageLoader, FullPageState } from "@/components/FullPageState";
import { previewLabel, usePreview, usePreviewControls } from "@/lib/preview";
import { ApiError } from "@/lib/restClient";

export function AccountLoadError({
  title,
  error,
  onRetry,
}: {
  title: string;
  error: Error;
  onRetry: () => void;
}) {
  const auth = useAuth();
  const location = useLocation();
  const preview = usePreview();
  const controls = usePreviewControls();
  const attemptedRenewal = useRef(false);
  const [renewing, setRenewing] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [reconnectFailed, setReconnectFailed] = useState(false);
  const [needsSignin, setNeedsSignin] = useState(false);
  const [awaitingRenewalError, setAwaitingRenewalError] = useState(false);
  const [renewalFailed, setRenewalFailed] = useState(false);
  const [renewalAttempt, setRenewalAttempt] = useState(0);
  const status = error instanceof ApiError ? error.status : undefined;
  const busy = renewing || reconnecting || !!auth.activeNavigator || auth.isLoading;
  const returnTo = safeReturnTo(pathOf(location)) ?? returnToFromState(auth.user?.state) ?? "/home";
  const { signinSilent, removeUser } = auth;
  const refreshToken = auth.user?.refresh_token;

  useEffect(() => {
    if (
      preview ||
      status !== 401 ||
      attemptedRenewal.current ||
      auth.isLoading ||
      auth.activeNavigator
    )
      return;
    attemptedRenewal.current = true;
    setRenewing(true);
    async function recoverSession() {
      if (refreshToken) {
        try {
          const user = await signinSilent();
          if (user && !user.expired) {
            onRetry();
            return;
          }
          setAwaitingRenewalError(true);
          return;
        } catch (error) {
          if (!requiresSignin(error)) {
            setRenewalFailed(true);
            return;
          }
        }
      }
      await removeUser();
      setNeedsSignin(true);
    }
    void recoverSession()
      .catch(() => setReconnectFailed(true))
      .finally(() => setRenewing(false));
  }, [
    preview,
    status,
    auth.isLoading,
    auth.activeNavigator,
    signinSilent,
    removeUser,
    onRetry,
    refreshToken,
    renewalAttempt,
  ]);

  useEffect(() => {
    if (!awaitingRenewalError || auth.isLoading || auth.activeNavigator) return;
    setAwaitingRenewalError(false);
    if (requiresSignin(auth.error)) {
      void removeUser()
        .then(() => setNeedsSignin(true))
        .catch(() => setReconnectFailed(true));
    } else setRenewalFailed(true);
  }, [awaitingRenewalError, auth.isLoading, auth.activeNavigator, auth.error, removeUser]);

  function retry() {
    if (status !== 401) {
      onRetry();
      return;
    }
    attemptedRenewal.current = false;
    setRenewalFailed(false);
    setRenewalAttempt((value) => value + 1);
  }

  async function reconnect() {
    if (busy) return;
    setReconnecting(true);
    setReconnectFailed(false);
    try {
      forgetSession();
      await auth.removeUser();
      window.location.assign(`/login?${new URLSearchParams({ returnTo })}`);
    } catch {
      setReconnectFailed(true);
      setReconnecting(false);
    }
  }

  if (preview)
    return (
      <div role="alert">
        <FullPageState
          title={`${previewLabel(preview)} could not be previewed`}
          description="The account could not be loaded as it is now. Stop previewing to get back to your own view."
          action={{ label: "Stop previewing", onClick: controls.stop }}
        />
      </div>
    );

  if (needsSignin && !busy) return <Navigate to="/login" replace state={{ returnTo }} />;
  if (status === 401 && !renewalFailed && !reconnectFailed)
    return <FullPageLoader label="Refreshing your session" />;

  const description =
    status === 401
      ? renewalFailed
        ? "Your session could not be refreshed. Check your connection and retry."
        : "Refreshing your session..."
      : status === 403
        ? "The server refused access. Sign in again to refresh your session. If access is still refused, contact your administrator."
        : "The server did not answer as expected. Check your connection and retry, or sign in again.";

  return (
    <div role="alert">
      <FullPageState title={title} description={description}>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          {status !== 403 && (status !== 401 || renewalFailed) && (
            <Button variant="secondary" onClick={retry} disabled={busy}>
              <RefreshCw className="size-4" aria-hidden="true" />
              Retry
            </Button>
          )}
          <Button
            onClick={() => void reconnect()}
            disabled={busy}
            loading={reconnecting}
            loadingLabel="Opening sign-in..."
          >
            <LogIn className="size-4" aria-hidden="true" />
            Sign in again
          </Button>
        </div>
        {reconnectFailed && (
          <p className="mt-3 text-sm text-secondary">
            Sign-in could not be restarted. Please try again.
          </p>
        )}
      </FullPageState>
    </div>
  );
}
